import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkProductionCompleteResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

import { updateProgressCache } from './progressCacheUtils';


// Mark production complete triggers stage change + optimistic progress generation on the backend.
// The default 15s apiClient timeout is insufficient and causes false errors.
const MARK_PRODUCTION_COMPLETE_TIMEOUT_MS = 60_000;

/**
 * API function to mark vehicle production as complete
 * @param servicesProviderId - The services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const markProductionComplete = async (
	servicesProviderId: string,
	orderId: string,
): Promise<MarkProductionCompleteResponse> => {
	const response = await apiClient.post<MarkProductionCompleteResponse>(
		ordersEndpoints.markProductionComplete(servicesProviderId, orderId),
		{}, // Empty body as per backend schema
		{ timeout: MARK_PRODUCTION_COMPLETE_TIMEOUT_MS },
	);
	return response.data;
};

/**
 * Hook to mark vehicle production as complete
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkProductionComplete = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark production as complete without an order ID or services provider ID.',
				);
			}
			return markProductionComplete(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.MARK_PRODUCTION_COMPLETE);
		},
		onError: () => {
			if (!orderId || !servicesProviderId) return;

			// Invalidate progress queries so the user sees fresh data on retry,
			// even if the backend actually succeeded (e.g. client timeout)
			queryClient.invalidateQueries({
				queryKey: queries.orders.detail(servicesProviderId, orderId)._ctx.progress
					.queryKey,
			});
		},
	});
};
